import { useEffect, useState, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Grid } from '@react-three/drei'
import { useDcStore } from '../stores/dcStore'
import { fetchRacks } from '../utils/layoutApi'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { useTheme } from '../hooks/useTheme'
import type { DatacenterConfig, RackConfig } from '../types'
import { Loader2 } from 'lucide-react'
import * as THREE from 'three'
import { topologyRackPose, topologyTrayScene, rotatedRectBounds, topologyYawRad } from '../utils/floorPlan.logic'

function RacksAndLeds({
  dcConfig,
  racksConfig,
  isDark,
}: {
  dcConfig: DatacenterConfig
  racksConfig: RackConfig[]
  isDark: boolean
}) {
  const racksRef = useRef<THREE.InstancedMesh>(null)
  const ledsRef = useRef<THREE.InstancedMesh>(null)
  const count = racksConfig.length

  useEffect(() => {
    if (!racksRef.current || !ledsRef.current || count === 0) return

    const dummyRack = new THREE.Object3D()
    const dummyLed = new THREE.Object3D()
    const colorObj = new THREE.Color()

    racksConfig.forEach((rack, instanceIdx) => {
      const pose = topologyRackPose(rack)
      const isBlocked = String(rack.is_blocked) === 'true' || String(rack.is_blocked) === '1' || rack.is_blocked === true
      const currentRackH = rack.height_m || dcConfig.rack_height_m || 2.0
      const yaw = topologyYawRad(pose.rotation)
      const cx = pose.x + pose.w / 2
      const cz = pose.z + pose.d / 2

      dummyRack.position.set(cx, currentRackH / 2, cz)
      dummyRack.rotation.set(0, yaw, 0)
      dummyRack.scale.set(pose.w, currentRackH, pose.d)
      dummyRack.updateMatrix()
      racksRef.current!.setMatrixAt(instanceIdx, dummyRack.matrix)

      const ledOff = pose.d / 2 + 0.05
      dummyLed.position.set(cx + ledOff * Math.sin(yaw), currentRackH / 2, cz + ledOff * Math.cos(yaw))
      dummyLed.rotation.set(0, yaw, 0)
      dummyLed.scale.set(pose.w * 0.9, currentRackH * 0.95, 0.05)
      dummyLed.updateMatrix()
      ledsRef.current!.setMatrixAt(instanceIdx, dummyLed.matrix)

      if (isBlocked) {
        racksRef.current!.setColorAt(instanceIdx, colorObj.setHex(isDark ? 0x991b1b : 0xb91c1c))
        ledsRef.current!.setColorAt(instanceIdx, colorObj.setHex(isDark ? 0xf87171 : 0xef4444))
      } else {
        racksRef.current!.setColorAt(instanceIdx, colorObj.setHex(isDark ? 0x1e293b : 0x475569))
        ledsRef.current!.setColorAt(instanceIdx, colorObj.setHex(isDark ? 0x0284c7 : 0x0284c7))
      }
    })

    racksRef.current.instanceMatrix.needsUpdate = true
    if (racksRef.current.instanceColor) racksRef.current.instanceColor.needsUpdate = true
    ledsRef.current.instanceMatrix.needsUpdate = true
    if (ledsRef.current.instanceColor) ledsRef.current.instanceColor.needsUpdate = true
  }, [dcConfig, racksConfig, isDark, count])

  if (count === 0) return null

  return (
    <group>
      <instancedMesh ref={racksRef} args={[null as any, null as any, count]} castShadow receiveShadow>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial metalness={0.5} roughness={0.5} />
      </instancedMesh>
      <instancedMesh ref={ledsRef} args={[null as any, null as any, count]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial />
      </instancedMesh>
    </group>
  )
}

function TraysGroup({
  dcConfig,
  isDark,
}: {
  dcConfig: DatacenterConfig
  isDark: boolean
}) {
  const meshData = useMemo(() => {
    let blob = dcConfig.cable_trays_json
    if (typeof blob === 'string') {
      try { blob = JSON.parse(blob) } catch { blob = { nodes: [], edges: [] } }
    }
    const elev = (dcConfig.rack_height_m || 2) + (dcConfig.tray_above_rack_m || 0.3)
    const scene = topologyTrayScene(blob, elev)
    const trayHeight = 0.15
    const trayWidth = 0.4
    const meshes: { key: string; position: [number, number, number]; rotation: [number, number, number]; size: [number, number, number] }[] = []

    scene.edges.forEach((e, i) => {
      const dx = e.x2 - e.x1
      const dz = e.z2 - e.z1
      const length = Math.sqrt(dx * dx + dz * dz)
      meshes.push({
        key: `edge-${i}`,
        position: [(e.x1 + e.x2) / 2, e.y, (e.z1 + e.z2) / 2],
        rotation: [0, -Math.atan2(dz, dx), 0],
        size: [length, trayHeight, trayWidth],
      })
    })
    scene.nodes.forEach((n, i) => {
      meshes.push({
        key: `node-${i}`,
        position: [n.x, n.y, n.z],
        rotation: [0, 0, 0],
        size: [trayWidth, trayHeight + 0.05, trayWidth],
      })
    })
    return meshes
  }, [dcConfig])

  return (
    <group>
      {meshData.map((data) => (
        <mesh key={data.key} position={data.position} rotation={data.rotation}>
          <boxGeometry args={data.size} />
          <meshBasicMaterial color={isDark ? '#334155' : '#475569'} wireframe transparent opacity={0.5} />
        </mesh>
      ))}
    </group>
  )
}

function hallCenter(racks: RackConfig[]): { cx: number; cz: number } {
  if (!racks.length) return { cx: 0, cz: 0 }
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity
  for (const r of racks) {
    const p = topologyRackPose(r)
    const b = rotatedRectBounds({ x: p.x, y: p.z, w: p.w, h: p.d, rotation: p.rotation })
    minX = Math.min(minX, b.minX)
    maxX = Math.max(maxX, b.maxX)
    minZ = Math.min(minZ, b.minY)
    maxZ = Math.max(maxZ, b.maxY)
  }
  return { cx: (minX + maxX) / 2, cz: (minZ + maxZ) / 2 }
}

function Scene({
  dcConfig,
  racksConfig,
  isDark,
}: {
  dcConfig: DatacenterConfig
  racksConfig: RackConfig[]
  isDark: boolean
}) {
  const { cx, cz } = useMemo(() => hallCenter(racksConfig), [racksConfig])

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[50, 100, 50]} intensity={1.2} />
      <Grid
        infiniteGrid
        fadeDistance={200}
        sectionColor={isDark ? '#374151' : '#d1d5db'}
        cellColor={isDark ? '#4b5563' : '#e5e7eb'}
        position={[cx, 0.01, cz]}
      />
      <RacksAndLeds dcConfig={dcConfig} racksConfig={racksConfig} isDark={isDark} />
      <TraysGroup dcConfig={dcConfig} isDark={isDark} />
      <OrbitControls target={[cx, 5, cz]} makeDefault dampingFactor={0.05} maxPolarAngle={Math.PI / 2 - 0.05} minDistance={5} maxDistance={200} />
    </>
  )
}

export default function TopologyPage() {
  const { dcCode } = useParams<{ dcCode: string }>()
  const { datacenters, activeDcCode } = useDcStore()
  const { activeWorkspaceId } = useWorkspace()
  const { isDark } = useTheme()

  const currentDc = dcCode || activeDcCode || ''
  const dcConfig = datacenters.find((d) => d.code === currentDc)

  const [racks, setRacks] = useState<RackConfig[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!currentDc) return
    setLoading(true)
    fetchRacks(currentDc, activeWorkspaceId)
      .then(setRacks)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [currentDc, activeWorkspaceId])

  if (!dcConfig) {
    return (
      <div className="h-full flex items-center justify-center bg-bg-app text-text-muted">
        Выберите дата-центр
      </div>
    )
  }

  const bgColor = isDark ? '#0B0E14' : '#F3F4F6'

  return (
    <div className="flex-1 min-h-[600px] h-full flex flex-col bg-bg-app relative">
      <div className="absolute top-4 left-4 z-10 bg-bg-panel/80 backdrop-blur border border-border px-4 py-2 rounded shadow-lg pointer-events-none">
        <h2 className="text-sm font-semibold text-text-primary">{dcConfig.code}</h2>
        <div className="text-xs text-text-muted mt-1">
          {racks.length} стоек загружено
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-bg-app/50 backdrop-blur-sm">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      )}

      <div className="flex-1 w-full h-full">
        <Canvas camera={{ position: [30, 40, 50], fov: 45 }} shadows={false}>
          <color attach="background" args={[bgColor]} />
          {!loading && <Scene dcConfig={dcConfig} racksConfig={racks} isDark={isDark} />}
        </Canvas>
      </div>
    </div>
  )
}
